import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, CheckCircle, XCircle, RefreshCw, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

const API_BASE_URL = window.location.origin + '/api'

export default function EmailVerificationPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [token, setToken] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [verificationStatus, setVerificationStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 페이지 로드 시 URL에서 토큰과 이메일 확인
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token')
    const emailFromUrl = searchParams.get('email')

    if (emailFromUrl) {
      setEmail(decodeURIComponent(emailFromUrl))
    }

    if (tokenFromUrl) {
      setToken(tokenFromUrl)
      handleVerifyToken(tokenFromUrl)
    }
  }, [searchParams])

  const handleVerifyToken = async (verificationToken: string) => {
    setIsVerifying(true)
    setVerificationStatus('idle')

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: verificationToken }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data.message || data.error || t('verifyEmail.verifyFailed')
        )
      }

      setVerificationStatus('success')
      toast.success(t('verifyEmail.verified'))

      // 3초 후 로그인 페이지로 이동
      setTimeout(() => {
        navigate('/login', {
          state: { message: t('verifyEmail.verifiedLogin') },
        })
      }, 3000)
    } catch (error) {
      console.error('Email verification error:', error)
      setVerificationStatus('error')
      setErrorMessage(
        error instanceof Error ? error.message : t('verifyEmail.verifyFailed')
      )
      toast.error(
        error instanceof Error ? error.message : t('verifyEmail.verifyFailed')
      )
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResendVerification = async () => {
    if (!email) {
      toast.error(t('verifyEmail.emailRequired'))
      return
    }

    setIsResending(true)
    try {
      const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(
          data.message || data.error || t('verifyEmail.resendError')
        )
      }

      toast.success(t('verifyEmail.resendSuccess'))
    } catch (error) {
      console.error('Resend email error:', error)
      toast.error(
        error instanceof Error ? error.message : t('verifyEmail.resendError')
      )
    } finally {
      setIsResending(false)
    }
  }

  const handleManualVerification = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) {
      toast.error(t('verifyEmail.tokenRequired'))
      return
    }
    await handleVerifyToken(token)
  }

  return (
    <div className="flex min-h-screen flex-col justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 dark:from-gray-900 dark:to-gray-800 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="https://shorty.kr" className="flex justify-center">
          <div className="flex items-center space-x-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
              <span className="text-lg font-bold text-white">S</span>
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              Shorty.kr
            </span>
          </div>
        </Link>

        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          {t('verifyEmail.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          {t('verifyEmail.subtitle')}
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
      >
        <div className="bg-white px-4 py-8 shadow-xl dark:bg-gray-800 sm:rounded-lg sm:px-10">
          {/* 인증 성공 */}
          {verificationStatus === 'success' && (
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 150 }}
              >
                <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
              </motion.div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                {t('verifyEmail.completeTitle')}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {t('verifyEmail.success')}
              </p>
              <div className="mt-4">
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {t('verifyEmail.goToLogin')}
                </Link>
              </div>
            </div>
          )}

          {/* 인증 실패 */}
          {verificationStatus === 'error' && (
            <div className="text-center">
              <XCircle className="mx-auto h-16 w-16 text-red-500" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                {t('verifyEmail.failed')}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {errorMessage}
              </p>
              <div className="mt-4 space-y-3">
                <button
                  onClick={() => setVerificationStatus('idle')}
                  className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  {t('verifyEmail.retry')}
                </button>
                <Link
                  to="/login"
                  className="block text-center text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                >
                  {t('verifyEmail.backToLogin')}
                </Link>
              </div>
            </div>
          )}

          {/* 인증 대기/입력 폼 */}
          {verificationStatus === 'idle' && (
            <div className="space-y-6">
              {isVerifying ? (
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                    {t('verifyEmail.processing')}
                  </p>
                </div>
              ) : (
                <>
                  {/* 이메일 재발송 섹션 */}
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                    <div className="flex items-start space-x-3">
                      <Mail className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200">
                          {t('verifyEmail.notReceived')}
                        </h4>
                        <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                          {t('verifyEmail.spamHint')}
                        </p>
                        <div className="mt-3 space-y-3">
                          <input
                            type="email"
                            placeholder={t('verifyEmail.emailPlaceholder')}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
                          />
                          <button
                            onClick={handleResendVerification}
                            disabled={isResending}
                            className="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isResending ? (
                              <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                                {t('verifyEmail.resending')}
                              </>
                            ) : (
                              <>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                {t('verifyEmail.resend')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 수동 인증 섹션 */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white px-2 text-gray-500 dark:bg-gray-800">
                        {t('verifyEmail.or')}
                      </span>
                    </div>
                  </div>

                  <form
                    onSubmit={handleManualVerification}
                    className="space-y-4"
                  >
                    <div>
                      <label
                        htmlFor="token"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        {t('verifyEmail.tokenLabel')}
                      </label>
                      <div className="mt-1">
                        <input
                          id="token"
                          name="token"
                          type="text"
                          value={token}
                          onChange={e => setToken(e.target.value)}
                          placeholder={t('verifyEmail.tokenPlaceholder')}
                          className="block w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('verifyEmail.hint')}
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isVerifying || !token}
                      className="flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('verifyEmail.submit')}
                    </button>
                  </form>
                </>
              )}
            </div>
          )}

          {/* 하단 링크 */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t('verifyEmail.backToLogin')}
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
